// redux/slices/clientsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl, getAuthHeaders } from '../../Utils/serverSelector';

// ============ Thunks ============

// Получить клиентов (private: /clients/:uid)
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.get(`${baseUrl}/clients/${uid}`, {
        headers: authHeaders,
        withCredentials: true,
        validateStatus: () => true,
      });

      if (res.status === 401) {
        throw new Error('Unauthorized (401) при загрузке клиентов');
      }

      if (res.status === 404) {
        // нет клиентов — трактуем как []
        return [];
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message || `Ошибка при загрузке клиентов (HTTP ${res.status})`
        );
      }

      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Ошибка при загрузке клиентов';
      return rejectWithValue(msg);
    }
  }
);

// Добавить клиента (private: POST /clients)
export const addClient = createAsyncThunk(
  'clients/addClient',
  async ({ uid, client }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      if (!client || !client.phoneContact) {
        throw new Error('Отсутствует информация о клиенте');
      }

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.post(
        `${baseUrl}/clients`,
        { ...client, uid },
        {
          headers: authHeaders,
          withCredentials: true,
          validateStatus: () => true,
        }
      );

      if (res.status === 401) {
        throw new Error('Unauthorized (401) при добавлении клиента');
      }

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          `Ошибка при добавлении клиента (HTTP ${res.status})`
        );
      }

      // сервер может вернуть { client: {...} } или просто {...}
      return res.data;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Ошибка при добавлении клиента';
      return rejectWithValue(msg);
    }
  }
);

// Удалить конкретного клиента по ID (private: DELETE /clients/id/:clientId)
export const deleteClient = createAsyncThunk(
  "clients/deleteClient",
  async ({ uid, clientId }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!clientId) throw new Error("ID клиента не передан");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const authHeaders = await getAuthHeaders();

      const res = await axios.delete(
        `${baseUrl}/clients/id/${encodeURIComponent(clientId)}`,
        {
          headers: authHeaders,
          withCredentials: true,
          params: { uid },
          validateStatus: () => true,
        }
      );

      if (res.status === 401) {
        throw new Error("Unauthorized (401) при удалении клиента");
      }

      if (res.status === 404) {
        throw new Error(
          res.data?.message || res.data?.error || "Клиент не найден"
        );
      }

      if (res.status !== 200 && res.status !== 204) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Ошибка при удалении клиента (HTTP ${res.status})`
        );
      }

      return Number(res.data?.clientId || clientId);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Ошибка при удалении клиента"
      );
    }
  }
);

// Обновить флаг предоплаты (private: PATCH /clients/prepay)
export const updateClientPrepay = createAsyncThunk(
  'clients/updateClientPrepay',
  async ({ uid, phoneContact, prepayRequired }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) {
        throw new Error('UID и phoneContact обязательны');
      }

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/prepay`,
        { uid, phoneContact, prepayRequired },
        {
          headers: authHeaders,
          withCredentials: true,
          validateStatus: () => true,
        }
      );

      if (res.status === 401) {
        throw new Error('Unauthorized (401) при обновлении предоплаты');
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Ошибка при обновлении предоплаты (HTTP ${res.status})`
        );
      }

      return res.data; // {client: {...}} или {...}
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Ошибка при обновлении предоплаты';
      return rejectWithValue(msg);
    }
  }
);

// Обновить задолженность клиента (private: PATCH /clients/debt)
export const updateClientDebt = createAsyncThunk(
  'clients/updateClientDebt',
  async ({ uid, phoneContact, debtAmount }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) {
        throw new Error('UID и phoneContact обязательны');
      }

      const normalizedDebt = Number(debtAmount);

      if (!Number.isFinite(normalizedDebt) || normalizedDebt < 0) {
        throw new Error(
          'Сумма задолженности должна быть числом не меньше 0'
        );
      }

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/debt`,
        {
          uid,
          phoneContact,
          debtAmount: normalizedDebt,
        },
        {
          headers: authHeaders,
          withCredentials: true,
          validateStatus: () => true,
        }
      );

      if (!res || !res.data) {
        throw new Error('Нет данных от сервера');
      }

      if (res.status === 401) {
        throw new Error('Unauthorized (401) при обновлении задолженности');
      }

      if (res.status === 404) {
        throw new Error('Клиент не найден');
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.error ||
          res.data?.message ||
          `Ошибка при обновлении задолженности (HTTP ${res.status})`
        );
      }

      return res.data?.client ?? res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка при обновлении задолженности клиента'
      );
    }
  }
);

// Обновить день рождения (private: PATCH /clients/birthday)
export const updateClientBirthday = createAsyncThunk(
  'clients/updateClientBirthday',
  async ({ uid, phoneContact, birthDate }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) {
        throw new Error('UID и phoneContact обязательны');
      }

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/birthday`,
        { uid, phoneContact, birthDate },
        {
          headers: authHeaders,
          withCredentials: true,
          validateStatus: () => true,
        }
      );

      if (res.status === 401) {
        throw new Error('Unauthorized (401) при обновлении дня рождения');
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Ошибка при обновлении дня рождения (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Ошибка при обновлении дня рождения';
      return rejectWithValue(msg);
    }
  }
);

// Обновить чёрный список (private: PATCH /clients/blacklist)
export const updateClientBlacklist = createAsyncThunk(
  'clients/updateClientBlacklist',
  async ({ uid, phoneContact, isBlacklisted }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) {
        throw new Error('UID и phoneContact обязательны');
      }

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/blacklist`,
        { uid, phoneContact, isBlacklisted },
        {
          headers: authHeaders,
          withCredentials: true,
          validateStatus: () => true,
        }
      );

      if (res.status === 401) {
        throw new Error('Unauthorized (401) при обновлении чёрного списка');
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Ошибка при обновлении чёрного списка (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Ошибка при обновлении чёрного списка';
      return rejectWithValue(msg);
    }
  }
);

/* ════════════════════════════════════════════════════════════════════════
   NEW (перенос с мобилки) — name / phone / language / instagram /
   source / discount-tiers / tags
   Бэкенд (NUC) эти эндпоинты уже поддерживает (используются мобилкой).
════════════════════════════════════════════════════════════════════════ */

// PATCH /clients/name  (имя / фамилия / заметки)
export const updateClientName = createAsyncThunk(
  'clients/updateClientName',
  async ({ uid, phoneContact, firstNameContact, lastNameContact, notesContact }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) throw new Error('UID и phoneContact обязательны');

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/name`,
        { uid, phoneContact, firstNameContact, lastNameContact, notesContact },
        { headers: authHeaders, withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 401) throw new Error('Unauthorized (401) при обновлении имени');
      if (res.status === 404) throw new Error('Клиент не найден');
      if (res.status !== 200) {
        throw new Error(res.data?.error || res.data?.message || `Ошибка при обновлении имени (HTTP ${res.status})`);
      }

      return res.data; // { client: {...} } или {...}
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Ошибка при обновлении имени';
      return rejectWithValue(msg);
    }
  }
);

// PATCH /clients/phone  (смена номера — меняется ключ phoneContact)
export const updateClientPhone = createAsyncThunk(
  'clients/updateClientPhone',
  async ({ uid, phoneContact, newPhoneContact }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact || !newPhoneContact) {
        throw new Error('UID, phoneContact и newPhoneContact обязательны');
      }

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/phone`,
        { uid, phoneContact, newPhoneContact },
        { headers: authHeaders, withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 401) throw new Error('Unauthorized (401) при обновлении номера');
      if (res.status === 404) throw new Error('Клиент не найден');
      if (res.status === 409) throw new Error('Клиент с таким номером уже существует');
      if (res.status !== 200) {
        throw new Error(res.data?.error || res.data?.message || `Ошибка при обновлении номера (HTTP ${res.status})`);
      }

      const updated = res.data?.client ?? res.data;
      // храним старый ключ, чтобы найти запись в стейте до подмены номера
      return { ...updated, oldPhoneContact: phoneContact };
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Ошибка при обновлении номера телефона';
      return rejectWithValue(msg);
    }
  }
);

// PATCH /clients/language
export const updateClientLanguage = createAsyncThunk(
  'clients/updateClientLanguage',
  async ({ uid, phoneContact, language }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) throw new Error('UID и phoneContact обязательны');

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/language`,
        { uid, phoneContact, language },
        { headers: authHeaders, withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 401) throw new Error('Unauthorized (401) при обновлении языка');
      if (res.status === 404) throw new Error('Клиент не найден');
      if (res.status !== 200) {
        throw new Error(res.data?.error || res.data?.message || `Ошибка при обновлении языка (HTTP ${res.status})`);
      }

      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Ошибка при обновлении языка клиента';
      return rejectWithValue(msg);
    }
  }
);

// PATCH /clients/instagram
export const updateClientInstagram = createAsyncThunk(
  'clients/updateClientInstagram',
  async ({ uid, phoneContact, instagramUrl }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) throw new Error('UID и phoneContact обязательны');

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/instagram`,
        { uid, phoneContact, instagramUrl },
        { headers: authHeaders, withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 401) throw new Error('Unauthorized (401) при обновлении Instagram');
      if (res.status === 404) throw new Error('Клиент не найден');
      if (res.status !== 200) {
        throw new Error(res.data?.error || res.data?.message || `Ошибка при обновлении Instagram (HTTP ${res.status})`);
      }

      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Ошибка при обновлении Instagram';
      return rejectWithValue(msg);
    }
  }
);

// PATCH /clients/source  (источник клиента)
// PATCH /clients/source
export const updateClientSource = createAsyncThunk(
  'clients/updateClientSource',
  async (
    { uid, clientId, phoneContact, clientSource },
    { rejectWithValue, requestId }
  ) => {
    const tag = `[ClientSource:${requestId}]`;
    const startedAt = Date.now();

    // До проверки — увидим, если экран не передаёт ID.
    console.info(`${tag} START`, {
      uid,
      clientId,
      hasPhone: Boolean(phoneContact),
      clientSource,
    });

    try {
      if (!uid) {
        throw new Error('UID не передан');
      }

      if (clientId == null || String(clientId).trim() === '') {
        throw new Error('ID клиента не передан из карточки');
      }

      const baseUrl = getServerUrl();
      if (!baseUrl) {
        throw new Error('API server URL is not configured');
      }

      const url = `${baseUrl.replace(/\/+$/, '')}/clients/source`;
      const headers = await getAuthHeaders();

      console.info(`${tag} REQUEST`, {
        method: 'PATCH',
        url,
        uid,
        clientId,
        hasPhone: Boolean(phoneContact),
        clientSource,
      });

      const res = await axios.patch(
        url,
        { uid, clientId, phoneContact, clientSource },
        {
          headers,
          withCredentials: true,
          validateStatus: () => true,
        }
      );

      console.info(`${tag} RESPONSE`, {
        status: res.status,
        durationMs: Date.now() - startedAt,
        contentType: res.headers?.['content-type'],
        clientId: res.data?.client?.id,
        ownerUid: res.data?.client?.uid,
      });

      if (res.status !== 200) {
        console.error(`${tag} SERVER ERROR`, {
          status: res.status,
          body: res.data,
        });

        throw new Error(
          res.data?.error ||
          res.data?.message ||
          `Ошибка обновления источника (HTTP ${res.status})`
        );
      }

      console.info(`${tag} SUCCESS`);
      return res.data;
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка обновления источника клиента';

      console.error(`${tag} FAILED`, {
        message,
        code: error?.code,
        durationMs: Date.now() - startedAt,
      });

      return rejectWithValue(message);
    }
  }
);

// PATCH /clients/discount-tiers  (карта лояльности)
export const updateClientDiscountTiers = createAsyncThunk(
  'clients/updateClientDiscountTiers',
  async ({ uid, phoneContact, discountTiers }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) throw new Error('UID и phoneContact обязательны');

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/discount-tiers`,
        { uid, phoneContact, discountTiers },
        { headers: authHeaders, withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 401) throw new Error('Unauthorized (401) при обновлении тиров');
      if (res.status === 404) throw new Error('Клиент не найден');
      if (res.status !== 200) {
        throw new Error(res.data?.error || res.data?.message || `Ошибка при обновлении тиров (HTTP ${res.status})`);
      }

      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Ошибка при обновлении скидочных тиров';
      return rejectWithValue(msg);
    }
  }
);

// PATCH /clients/tags  (теги + кастомные)
export const updateClientTags = createAsyncThunk(
  'clients/updateClientTags',
  async ({ uid, phoneContact, clientTags, customTagsData }, { rejectWithValue }) => {
    try {
      if (!uid || !phoneContact) throw new Error('UID и phoneContact обязательны');

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error('API server URL is not configured');

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/tags`,
        { uid, phoneContact, clientTags, customTagsData },
        { headers: authHeaders, withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 401) throw new Error('Unauthorized (401) при обновлении тегов');
      if (res.status === 404) throw new Error('Клиент не найден');
      if (res.status !== 200) {
        throw new Error(res.data?.error || res.data?.message || `Ошибка при обновлении тегов (HTTP ${res.status})`);
      }

      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Ошибка при обновлении тегов';
      return rejectWithValue(msg);
    }
  }
);

// === PUBLIC: получить клиентов одного мастера и записать И в плоский, и в byUid ===
export const fetchPublicClients = createAsyncThunk(
  'clients/fetchPublicClients',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      const base = (getServerUrl() || '').replace(/\/$/, '');
      if (!base) throw new Error('API server URL is not configured');

      const { data } = await axios.get(
        `${base}/public/${encodeURIComponent(uid)}/clients`,
        { validateStatus: () => true }
      );

      return {
        uid: String(uid),
        clients: Array.isArray(data) ? data : [],
      };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data || e?.message || 'Failed to fetch public clients'
      );
    }
  }
);

// === PUBLIC: только в кэш byUid (не трогаем плоский список) ===
export const fetchPublicClientsByUid = createAsyncThunk(
  'clients/fetchPublicClientsByUid',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      const base = (getServerUrl() || '').replace(/\/$/, '');
      if (!base) throw new Error('API server URL is not configured');

      const { data } = await axios.get(
        `${base}/public/${encodeURIComponent(uid)}/clients`,
        { validateStatus: () => true }
      );

      return {
        uid: String(uid),
        clients: Array.isArray(data) ? data : [],
      };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        'Failed to fetch public clients by uid'
      );
    }
  }
);

// === PUBLIC: батч для набора uid (пишет только в byUid) ===
export const fetchPublicClientsForUids = createAsyncThunk(
  'clients/fetchPublicClientsForUids',
  async (uidsArg = [], { rejectWithValue }) => {
    try {
      const list = Array.isArray(uidsArg) ? uidsArg : [uidsArg];
      const uniq = [...new Set(list.map((u) => String(u)).filter(Boolean))];
      if (!uniq.length) return { results: [], requested: [] };

      const base = (getServerUrl() || '').replace(/\/$/, '');
      if (!base) throw new Error('API server URL is not configured');

      const results = [];
      const CONCURRENCY = 3;

      for (let i = 0; i < uniq.length; i += CONCURRENCY) {
        const chunk = uniq.slice(i, i + CONCURRENCY);
        const settled = await Promise.allSettled(
          chunk.map(async (uid) => {
            const { data } = await axios.get(
              `${base}/public/${encodeURIComponent(uid)}/clients`,
              { validateStatus: () => true }
            );
            return { uid, clients: Array.isArray(data) ? data : [] };
          })
        );
        settled.forEach((r) => {
          if (r.status === 'fulfilled' && r.value) results.push(r.value);
        });
      }

      return { results, requested: uniq };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        'Failed to fetch public clients for uids'
      );
    }
  }
);

// PATCH /clients/receipt-name
export const updateClientReceiptName = createAsyncThunk(
  "clients/updateClientReceiptName",
  async (
    { uid, phoneContact, receiptName },
    { rejectWithValue }
  ) => {
    try {
      if (!uid || !phoneContact) {
        throw new Error(
          "UID и phoneContact обязательны"
        );
      }

      const baseUrl = getServerUrl();

      if (!baseUrl) {
        throw new Error(
          "API server URL is not configured"
        );
      }

      const authHeaders = await getAuthHeaders();

      const res = await axios.patch(
        `${baseUrl}/clients/receipt-name`,
        {
          uid,
          phoneContact,
          receiptName,
        },
        {
          headers: authHeaders,
          withCredentials: true,
          validateStatus: () => true,
        }
      );

      if (res.status === 401) {
        throw new Error(
          "Unauthorized (401) при обновлении имени для чека"
        );
      }

      if (res.status === 404) {
        throw new Error("Клиент не найден");
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.error ||
          res.data?.message ||
          `Ошибка при обновлении имени для чека (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Ошибка при обновлении имени для чека"
      );
    }
  }
);


// ============ Slice ============

const initialState = {
  clients: [],
  status: 'idle',
  error: null,

  byUid: {},        // { [uid]: Client[] }
  statusByUid: {},  // { [uid]: 'idle'|'loading'|'succeeded'|'failed' }
  errorByUid: {},   // { [uid]: string|null }
};

// утилита мерджа клиента по phoneContact
const upsertByPhone = (state, payload) => {
  const updated = payload?.client ?? payload; // поддержка обоих форматов ответа
  if (!updated || !updated.phoneContact) return;
  const idx = state.clients.findIndex(
    (c) => String(c.phoneContact) === String(updated.phoneContact)
  );
  if (idx !== -1) {
    state.clients[idx] = { ...state.clients[idx], ...updated };
  } else {
    state.clients.push(updated);
  }
};

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setClients: (state, action) => {
      state.clients = action.payload || [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchClients.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.clients = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload ||
          action.error?.message ||
          'Ошибка при загрузке клиентов';
      })

      // add
      .addCase(addClient.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(addClient.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const created = action.payload?.client ?? action.payload;
        if (created) state.clients.push(created);
      })
      .addCase(addClient.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload ||
          action.error?.message ||
          'Ошибка при добавлении клиента';
      })

      // delete
      .addCase(deleteClient.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const deletedClientId = Number(action.payload);

        state.clients = state.clients.filter(
          (client) => Number(client?.id) !== deletedClientId
        );
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload ||
          action.error?.message ||
          'Ошибка при удалении клиента';
      })

      // prepay
      .addCase(updateClientPrepay.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientPrepay.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientPrepay.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload ||
          action.error?.message ||
          'Ошибка при обновлении предоплаты';
      })

      // debt
      .addCase(updateClientDebt.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientDebt.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientDebt.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload ||
          action.error?.message ||
          'Ошибка при обновлении задолженности клиента';
      })

      // birthday
      .addCase(updateClientBirthday.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientBirthday.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientBirthday.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload ||
          action.error?.message ||
          'Ошибка при обновлении дня рождения';
      })

      // blacklist
      .addCase(updateClientBlacklist.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientBlacklist.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientBlacklist.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload ||
          action.error?.message ||
          'Ошибка при обновлении чёрного списка';
      })

      // ── NEW: name ──
      .addCase(updateClientName.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientName.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientName.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Ошибка при обновлении имени';
      })

      // ── NEW: phone (особый случай — меняется ключ phoneContact) ──
      .addCase(updateClientPhone.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientPhone.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updated = action.payload?.client ?? action.payload;
        if (!updated) return;
        const { oldPhoneContact, ...clientData } = updated;
        const idx = state.clients.findIndex(
          (c) => String(c.phoneContact) === String(oldPhoneContact)
        );
        if (idx !== -1) state.clients[idx] = { ...state.clients[idx], ...clientData };
        else if (clientData.phoneContact) state.clients.push(clientData);
      })
      .addCase(updateClientPhone.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Ошибка при обновлении номера';
      })

      // ── NEW: language ──
      .addCase(updateClientLanguage.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientLanguage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientLanguage.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Ошибка при обновлении языка';
      })

      // ── NEW: instagram ──
      .addCase(updateClientInstagram.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientInstagram.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientInstagram.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Ошибка при обновлении Instagram';
      })

      // ── NEW: source ──
      .addCase(updateClientSource.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClientSource.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;

        const updated = action.payload?.client ?? action.payload;
        if (!updated || updated.id == null) return;

        const ownerUid = updated.uid ?? action.meta.arg.uid;
        const mergeInto = (list) => {
          if (!Array.isArray(list)) return;
          const index = list.findIndex(
            (client) =>
              String(client.id) === String(updated.id) &&
              String(client.uid) === String(ownerUid)
          );
          if (index !== -1) {
            list[index] = { ...list[index], ...updated };
          }
        };

        mergeInto(state.clients);
        mergeInto(state.byUid[String(ownerUid)]);
      })
      .addCase(updateClientSource.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Ошибка при обновлении источника';
      })

      // ── NEW: discount tiers ──
      .addCase(updateClientDiscountTiers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientDiscountTiers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Ошибка при обновлении тиров';
      })

      // ── NEW: tags ──
      .addCase(updateClientTags.fulfilled, (state, action) => {
        state.status = 'succeeded';
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientTags.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Ошибка при обновлении тегов';
      })

      // PUBLIC: fetchPublicClients
      .addCase(fetchPublicClients.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        const uid = String(action.meta.arg || '');
        if (uid) {
          state.statusByUid[uid] = 'loading';
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicClients.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { uid, clients } = action.payload || {};
        state.clients = clients || [];
        if (uid) {
          state.byUid[uid] = clients || [];
          state.statusByUid[uid] = 'succeeded';
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicClients.rejected, (state, action) => {
        state.status = 'failed';
        const errMsg =
          action.payload ||
          action.error?.message ||
          'Failed to fetch public clients';
        state.error = errMsg;
        const uid = String(action.meta.arg || '');
        if (uid) {
          state.statusByUid[uid] = 'failed';
          state.errorByUid[uid] = errMsg;
          state.byUid[uid] = state.byUid[uid] || [];
        }
      })

      // PUBLIC: fetchPublicClientsByUid
      .addCase(fetchPublicClientsByUid.pending, (state, action) => {
        const uid = String(action.meta.arg || '');
        if (uid) {
          state.statusByUid[uid] = 'loading';
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicClientsByUid.fulfilled, (state, action) => {
        const { uid, clients } = action.payload || {};
        if (uid) {
          state.byUid[uid] = clients || [];
          state.statusByUid[uid] = 'succeeded';
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicClientsByUid.rejected, (state, action) => {
        const uid = String(action.meta.arg || '');
        const errMsg =
          action.payload ||
          action.error?.message ||
          'Failed to fetch public clients by uid';
        if (uid) {
          state.statusByUid[uid] = 'failed';
          state.errorByUid[uid] = errMsg;
        }
      })

      // PUBLIC: fetchPublicClientsForUids
      .addCase(fetchPublicClientsForUids.pending, (state, action) => {
        const uids = Array.isArray(action.meta.arg)
          ? action.meta.arg
          : [action.meta.arg];
        uids
          .map(String)
          .filter(Boolean)
          .forEach((uid) => {
            state.statusByUid[uid] = 'loading';
            state.errorByUid[uid] = null;
          });
      })
      .addCase(fetchPublicClientsForUids.fulfilled, (state, action) => {
        const { results = [], requested = [] } = action.payload || {};
        results.forEach(({ uid, clients }) => {
          const k = String(uid);
          state.byUid[k] = clients || [];
          state.statusByUid[k] = 'succeeded';
          state.errorByUid[k] = null;
        });
        requested.map(String).forEach((uid) => {
          if (state.statusByUid[uid] === 'loading') {
            state.statusByUid[uid] = state.byUid[uid] ? 'succeeded' : 'failed';
          }
        });
      })
      .addCase(fetchPublicClientsForUids.rejected, (state, action) => {
        const uids = Array.isArray(action.meta.arg)
          ? action.meta.arg
          : [action.meta.arg];
        const msg =
          action.payload ||
          action.error?.message ||
          'Failed to fetch public clients for uids';
        uids
          .map(String)
          .filter(Boolean)
          .forEach((uid) => {
            state.statusByUid[uid] = 'failed';
            state.errorByUid[uid] = msg;
          });
      })

      .addCase(updateClientReceiptName.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateClientReceiptName.fulfilled, (state, action) => {
        state.status = "succeeded";
        upsertByPhone(state, action.payload);
      })
      .addCase(updateClientReceiptName.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Ошибка при обновлении имени для чека";
      })

      // общий сброс
      .addCase(resetStore, () => initialState);
  },
});

export const { setClients } = clientsSlice.actions;
export default clientsSlice.reducer;
